import api from './api';
import {
  Task,
  TaskFormData,
  TaskComment,
  TaskLink,
  TaskAttachment,
  TaskWatcher,
  Component,
  Version,
} from '../types/task';

// Task Comments API
export const taskCommentsAPI = {
  getAll: (taskId: number) =>
    api.get<{ comments: TaskComment[] }>(`/tasks/${taskId}/comments`),

  create: (taskId: number, data: { body: string; parent_comment_id?: number }) =>
    api.post<{ comment: TaskComment }>(`/tasks/${taskId}/comments`, { comment: data }),

  update: (taskId: number, commentId: number, body: string) =>
    api.put<{ comment: TaskComment }>(`/tasks/${taskId}/comments/${commentId}`, { comment: { body } }),

  delete: (taskId: number, commentId: number) =>
    api.delete(`/tasks/${taskId}/comments/${commentId}`),
};

// Task Links API
export const taskLinksAPI = {
  getAll: (taskId: number) =>
    api.get<{ outgoing_links: TaskLink[]; incoming_links: TaskLink[] }>(`/tasks/${taskId}/links`),

  create: (taskId: number, targetTaskId: number, linkType: string) =>
    api.post(`/tasks/${taskId}/links`, { target_task_id: targetTaskId, link_type: linkType }),

  delete: (taskId: number, linkId: number) =>
    api.delete(`/tasks/${taskId}/links/${linkId}`),
};

// Task Watchers API
export const taskWatchersAPI = {
  getAll: (taskId: number) =>
    api.get<{ watchers: TaskWatcher[]; is_watching: boolean }>(`/tasks/${taskId}/watchers`),

  add: (taskId: number, userId?: number) =>
    api.post(`/tasks/${taskId}/watchers`, userId ? { user_id: userId } : {}),

  remove: (taskId: number, userId: number) =>
    api.delete(`/tasks/${taskId}/watchers/${userId}`),

  watch: (taskId: number) =>
    api.post<{ message: string; is_watching: boolean }>(`/tasks/${taskId}/watchers/watch`),

  unwatch: (taskId: number) =>
    api.delete<{ message: string; is_watching: boolean }>(`/tasks/${taskId}/watchers/unwatch`),
};

// Task Attachments API
export const taskAttachmentsAPI = {
  getAll: (taskId: number) =>
    api.get<{ attachments: TaskAttachment[] }>(`/tasks/${taskId}/attachments`),

  upload: (taskId: number, file: File, description?: string) => {
    const formData = new FormData();
    formData.append('attachment[filename]', file.name);
    formData.append('attachment[content_type]', file.type);
    formData.append('attachment[file_size]', file.size.toString());
    if (description) {
      formData.append('attachment[description]', description);
    }
    // Note: Actual file upload would need S3 or similar integration
    return api.post<{ attachment: TaskAttachment }>(`/tasks/${taskId}/attachments`, formData);
  },

  delete: (taskId: number, attachmentId: number) =>
    api.delete(`/tasks/${taskId}/attachments/${attachmentId}`),
};

// Components API
export const componentsAPI = {
  getAll: (projectId: number) =>
    api.get<{ components: Component[] }>(`/projects/${projectId}/components`),

  getById: (projectId: number, componentId: number) =>
    api.get<{ component: Component }>(`/projects/${projectId}/components/${componentId}`),

  create: (projectId: number, data: { name: string; description?: string; lead_user_id?: number }) =>
    api.post<{ component: Component }>(`/projects/${projectId}/components`, { component: data }),

  update: (projectId: number, componentId: number, data: Partial<Component>) =>
    api.put<{ component: Component }>(`/projects/${projectId}/components/${componentId}`, { component: data }),

  delete: (projectId: number, componentId: number) =>
    api.delete(`/projects/${projectId}/components/${componentId}`),
};

// Versions API
export const versionsAPI = {
  getAll: (projectId: number) =>
    api.get<{ versions: Version[] }>(`/projects/${projectId}/versions`),

  getById: (projectId: number, versionId: number) =>
    api.get<{ version: Version & { tasks: Task[] } }>(`/projects/${projectId}/versions/${versionId}`),

  create: (projectId: number, data: { name: string; description?: string; start_date?: string; release_date?: string }) =>
    api.post<{ version: Version }>(`/projects/${projectId}/versions`, { version: data }),

  update: (projectId: number, versionId: number, data: Partial<Version>) =>
    api.put<{ version: Version }>(`/projects/${projectId}/versions/${versionId}`, { version: data }),

  delete: (projectId: number, versionId: number) =>
    api.delete(`/projects/${projectId}/versions/${versionId}`),

  release: (projectId: number, versionId: number) =>
    api.post<{ version: Version }>(`/projects/${projectId}/versions/${versionId}/release`),

  archive: (projectId: number, versionId: number) =>
    api.post<{ version: Version }>(`/projects/${projectId}/versions/${versionId}/archive`),
};

// Extended Tasks API (for new actions)
export const tasksExtendedAPI = {
  flag: (taskId: number) =>
    api.patch<{ task: Task }>(`/tasks/${taskId}/flag`),

  unflag: (taskId: number) =>
    api.patch<{ task: Task }>(`/tasks/${taskId}/unflag`),

  resolve: (taskId: number, resolution: string) =>
    api.patch<{ task: Task }>(`/tasks/${taskId}/resolve`, { resolution }),
};

export default {
  comments: taskCommentsAPI,
  links: taskLinksAPI,
  watchers: taskWatchersAPI,
  attachments: taskAttachmentsAPI,
  components: componentsAPI,
  versions: versionsAPI,
  tasks: tasksExtendedAPI,
};
